import React from 'react';
import { Box, Typography, Paper, Divider, useMediaQuery } from '@mui/material';
import GroupRounded from '@mui/icons-material/GroupRounded';
import { TeamBadgeImage } from './ScoreHeader';

// ─── Lineup adapter ───────────────────────────────────────────────────────────

const POSITION_ROW = { goalkeeper: 0, defender: 1, midfielder: 2, forward: 3 };

function adaptLineupByPosition(starting) {
  const groups = { goalkeeper: [], defender: [], midfielder: [], forward: [] };
  for (const { player } of starting) {
    const pos = player?.position ?? 'midfielder';
    (groups[pos] ?? groups.midfielder).push(player);
  }
  return Object.entries(POSITION_ROW).flatMap(([pos, row]) =>
    groups[pos].map((player, i) => ({
      name: player.name,
      number: player.number,
      photo: player.photo,
      row,
      col: i + 1,
    }))
  );
}

export function adaptLineup(lineup) {
  const useGrid = lineup.starting.every(({ grid }) => grid != null);
  return {
    formation: lineup.formation,
    players: useGrid
      ? lineup.starting.map(({ grid, player }) => ({
          name: player.name,
          number: player.number,
          photo: player.photo,
          row: grid.y - 1,
          col: grid.x,
        }))
      : adaptLineupByPosition(lineup.starting),
  };
}

// ─── Player placeholder ───────────────────────────────────────────────────────

function PlayerPlaceholder({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" fill="#BDBDBD" xmlns="http://www.w3.org/2000/svg">
      <circle cx="40" cy="10" r="8" />
      <path d="M35 18 C30 22 24 28 20 34 L25 37 C28 32 33 27 36 24 L38 32 L34 44 L41 44 L44 30 L46 22 Z" />
      <path d="M20 34 L8 38 L9 44 L22 40 L25 37 Z" />
      <circle cx="6" cy="47" r="7" />
    </svg>
  );
}

// ─── Player dot ───────────────────────────────────────────────────────────────

function PlayerDot({ name, photoUrl }) {
  const [status, setStatus] = React.useState('loading');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.15)', bgcolor: '#f0f0f0', position: 'relative', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {status !== 'loaded' && <PlayerPlaceholder size={20} />}
        {photoUrl && (
          <Box
            component="img"
            src={photoUrl}
            alt={name}
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('error')}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: status === 'loaded' ? 1 : 0, transition: 'opacity 0.2s ease' }}
          />
        )}
      </Box>
      <Typography variant="caption" sx={{ textAlign: 'center', width: '100%', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name.split(' ').pop()}
      </Typography>
    </Box>
  );
}

// ─── Pitch SVG ────────────────────────────────────────────────────────────────

function PitchSvg({ vertical, dims }) {
  const stroke = '#AAA';
  const ir = 2;
  const stripeA = '#78788052';
  const stripeB = '#7676802E';
  const stripes = 5;
  const W = 110, H = 70, cx = 55, cy = 35;
  const sixW = 6, sixH2 = 10;
  const penW = 18, penH2 = 22;
  const spotX = 12;
  const dY1 = cy - 8, dY2 = cy + 8;

  // Actual pixel dimensions of the SVG element (swapped when vertical due to rotation)
  const svgPxW = dims ? (vertical ? dims.height : dims.width) : 0;
  const svgPxH = dims ? (vertical ? dims.width : dims.height) : 0;

  // Convert a target pixel radius into SVG-unit rx/ry so circles are always
  // the same physical size and perfectly circular regardless of pitch aspect ratio.
  const TARGET_CIRCLE_PX = 36;
  const TARGET_DOT_PX = 2.5;
  const crx = svgPxW ? TARGET_CIRCLE_PX * W / svgPxW : 10;
  const cry = svgPxH ? TARGET_CIRCLE_PX * H / svgPxH : 10;
  const drx = svgPxW ? TARGET_DOT_PX * W / svgPxW : 0.7;
  const dry = svgPxH ? TARGET_DOT_PX * H / svgPxH : 0.7;

  const vx = 'non-scaling-stroke';

  const content = (
    <>
      {Array.from({ length: stripes }, (_, i) => (
        <rect key={i} x={i * (W / stripes)} y="0" width={W / stripes} height={H} fill={i % 2 === 0 ? stripeA : stripeB} />
      ))}
      <line x1={cx} y1="0" x2={cx} y2={H} stroke={stroke} strokeWidth={1} vectorEffect={vx} />
      <ellipse cx={cx} cy={cy} rx={crx} ry={cry} fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} />
      <ellipse cx={cx} cy={cy} rx={drx} ry={dry} fill={stroke} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M0,${cy-sixH2} H${sixW-ir} Q${sixW},${cy-sixH2} ${sixW},${cy-sixH2+ir} V${cy+sixH2-ir} Q${sixW},${cy+sixH2} ${sixW-ir},${cy+sixH2} H0`} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M${W},${cy-sixH2} H${W-sixW+ir} Q${W-sixW},${cy-sixH2} ${W-sixW},${cy-sixH2+ir} V${cy+sixH2-ir} Q${W-sixW},${cy+sixH2} ${W-sixW+ir},${cy+sixH2} H${W}`} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M0,${cy-penH2} H${penW-ir} Q${penW},${cy-penH2} ${penW},${cy-penH2+ir} V${cy+penH2-ir} Q${penW},${cy+penH2} ${penW-ir},${cy+penH2} H0`} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M${W},${cy-penH2} H${W-penW+ir} Q${W-penW},${cy-penH2} ${W-penW},${cy-penH2+ir} V${cy+penH2-ir} Q${W-penW},${cy+penH2} ${W-penW+ir},${cy+penH2} H${W}`} />
      <ellipse cx={spotX} cy={cy} rx={drx} ry={dry} fill={stroke} />
      <ellipse cx={W-spotX} cy={cy} rx={drx} ry={dry} fill={stroke} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M${penW},${dY1} A${crx},${cry} 0 0 1 ${penW},${dY2}`} />
      <path fill="none" stroke={stroke} strokeWidth={1} vectorEffect={vx} d={`M${W-penW},${dY1} A${crx},${cry} 0 0 0 ${W-penW},${dY2}`} />
    </>
  );

  if (vertical && dims) {
    // Swap width/height so the landscape SVG fills a portrait container after rotation
    return (
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        sx={{
          position: 'absolute',
          width: dims.height,
          height: dims.width,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(90deg)',
          pointerEvents: 'none',
        }}>
        {content}
      </Box>
    );
  }

  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      {content}
    </Box>
  );
}

// ─── Pitch lineup ─────────────────────────────────────────────────────────────

function PitchLineup({ homeLineup, awayLineup }) {
  const isNarrow = useMediaQuery('(max-width: 450px)');

  const rows = Math.max(
    ...homeLineup.players.map(p => p.row),
    ...awayLineup.players.map(p => p.row),
  ) + 1;
  const homeByRow = Array.from({ length: rows }, (_, r) =>
    homeLineup.players.filter(p => p.row === r).sort((a, b) => a.col - b.col)
  ).filter(row => row.length > 0);
  const awayByRow = Array.from({ length: rows }, (_, r) =>
    awayLineup.players.filter(p => p.row === r)
  ).reverse().filter(row => row.length > 0);

  const containerRef = React.useRef(null);
  const [dims, setDims] = React.useState(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setDims({ width, height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (isNarrow) {
    // Vertical layout: home GK at top → home forwards → away forwards → away GK at bottom
    // Each team is confined to their half (50% of pitch height).
    return (
      <Box ref={containerRef} sx={{ position: 'relative', borderRadius: 2, borderColor: '#AAA', borderWidth: 1.5, borderStyle: 'solid', overflow: 'hidden', p: 1.5 }}>
        <PitchSvg vertical dims={dims} />
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 520 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {homeByRow.map((rowPlayers, ri) => (
              <Box key={`home-${ri}`} sx={{ flex: 1, display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                {rowPlayers.map((p, pi) => <PlayerDot key={pi} name={p.name} photoUrl={p.photo} />)}
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {awayByRow.map((rowPlayers, ri) => (
              <Box key={`away-${ri}`} sx={{ flex: 1, display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                {rowPlayers.map((p, pi) => <PlayerDot key={pi} name={p.name} photoUrl={p.photo} />)}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', borderRadius: 2, borderColor: '#AAA', borderWidth: 1.5, borderStyle: 'solid', overflow: 'hidden', p: 1.5 }}>
      <PitchSvg dims={dims} />
      <Box sx={{ position: 'relative', display: 'flex', gap: 1, minHeight: 220 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          {homeByRow.map((colPlayers, ci) => (
            <Box key={ci} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center', py: 1, gap: 3 }}>
              {colPlayers.map((p, pi) => (
                <PlayerDot key={pi} name={p.name} photoUrl={p.photo} />
              ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          {awayByRow.map((colPlayers, ci) => (
            <Box key={ci} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center', py: 1 }}>
              {colPlayers.map((p, pi) => (
                <PlayerDot key={pi} name={p.name} photoUrl={p.photo} />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Lineups card ─────────────────────────────────────────────────────────────

export default function LineupsCard({ fixture, homeLineup, awayLineup, homeColor, awayColor }) {
  return (
    <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <GroupRounded sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Lineups
        </Typography>
      </Box>
      <Divider sx={{ mb: 1.5, borderColor: 'divider' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TeamBadgeImage team={fixture.homeTeam} size={20} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{fixture.homeTeam.name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{fixture.awayTeam.name}</Typography>
          <TeamBadgeImage team={fixture.awayTeam} size={20} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>{homeLineup.formation}</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>{awayLineup.formation}</Typography>
      </Box>
      <PitchLineup homeLineup={homeLineup} awayLineup={awayLineup} homeColor={homeColor} awayColor={awayColor} />
    </Paper>
  );
}
